import { type ReactNode, memo, useCallback, useEffect, useState } from 'react';
import { type I18NLocale } from 'Locale';
import { DELAY_BETWEEN_PRETEND_MESSAGES_MS, PRETEND_MESSAGE_SPINNER_DURATION_MS } from 'TutorBotConversation';
import { useTriggerCallbackOnInterval } from 'useTriggerCallbackOnInterval';
import { TutorBotMessageContent } from 'TutorBotMessageContent';
import FrontRoyalSpinner from 'FrontRoyalSpinner';
import { type TFunction } from 'i18next';

function IntroMessage({ children, shouldShowSpinner }: { children: ReactNode; shouldShowSpinner: boolean }) {
    const [showSpinner, setShowSpinner] = useState(shouldShowSpinner);

    useEffect(() => {
        let timeout: NodeJS.Timeout;
        if (showSpinner) {
            timeout = setTimeout(() => {
                setShowSpinner(false);
            }, PRETEND_MESSAGE_SPINNER_DURATION_MS);
        }
        return () => clearTimeout(timeout);
    }, [shouldShowSpinner, showSpinner]);

    return (
        <TutorBotMessageContent
            className="first:mt-auto"
            isHumanMessage={false}
            contentType="text"
            isLoading={showSpinner}
            isOneSidedConversation
        >
            {showSpinner ? <FrontRoyalSpinner className="no-top-margin no-delay static-height m-0" /> : children}
        </TutorBotMessageContent>
    );
}

function IntroMessagesComponent({ messages, t }: { messages: I18NLocale[]; t: TFunction }) {
    const [visibleMessages, setVisibleMessages] = useState<I18NLocale[]>([]);

    const showNextMessage = useCallback((messageToAdd: I18NLocale) => {
        setVisibleMessages(prev => [...prev, messageToAdd]);
    }, []);

    useTriggerCallbackOnInterval({
        listToTrigger: messages,
        callback: showNextMessage,
        intervalMs: DELAY_BETWEEN_PRETEND_MESSAGES_MS,
    });

    // In the wild, the messages probably never change. But, if they do, it makes
    // the most sense to remove the ones that are on the screen before adding new
    // ones. This makes a difference in development with hot reloading.
    useEffect(() => {
        setVisibleMessages([]);
    }, [messages]);

    return (
        <>
            {visibleMessages.map(message => (
                <IntroMessage key={message.key} shouldShowSpinner={message === visibleMessages.at(-1)}>
                    {t(message.key, message.params || {})}
                </IntroMessage>
            ))}
        </>
    );
}

export const IntroMessages = memo(IntroMessagesComponent) as typeof IntroMessagesComponent;
